import type { PanelType } from "../components/reusable/PanelSelector";

/** `localStorage` (not sessionStorage): new tabs have separate sessionStorage. */
export const SVG_TOOL_HANDOFF_STORAGE_PREFIX = "svg-tool-handoff:v1:";

export type SvgToolHandoffSlot = "path" | "object" | "primary";

export type SvgToolHandoffPayloadV1 = {
    v: 1;
    targetPanel: PanelType;
    slot: SvgToolHandoffSlot;
    name: string;
    content: string;
};

function handoffStorageKey(id: string): string {
    return `${SVG_TOOL_HANDOFF_STORAGE_PREFIX}${id}`;
}

function randomHandoffId(): string {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

export function stripHandoffQueryFromUrl(): void {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (!url.searchParams.has("handoff")) return;
    url.searchParams.delete("handoff");
    const qs = url.searchParams.toString();
    const next = url.pathname + (qs ? `?${qs}` : "") + url.hash;
    window.history.replaceState(window.history.state, "", next);
}

function readHandoffIdFromUrl(): string | null {
    if (typeof window === "undefined") return null;
    return new URL(window.location.href).searchParams.get("handoff");
}

/**
 * Store payload and return id + full URL for navigation (query + hash).
 * Uses localStorage so a tab opened via `window.open` can read the same key.
 * Throws if storage quota exceeded.
 */
export function storeHandoffPayload(payload: SvgToolHandoffPayloadV1): {
    id: string;
    url: string;
} {
    const id = randomHandoffId();
    const key = handoffStorageKey(id);
    const serialized = JSON.stringify(payload);
    try {
        localStorage.setItem(key, serialized);
    } catch {
        throw new Error("Could not store SVG (browser storage may be full).");
    }
    const base = new URL(window.location.href);
    base.searchParams.set("handoff", id);
    base.hash = `#${payload.targetPanel}`;
    return { id, url: base.toString() };
}

export function buildHandoffUrl(payload: SvgToolHandoffPayloadV1): string {
    return storeHandoffPayload(payload).url;
}

export function openHandoffInNewTab(payload: SvgToolHandoffPayloadV1): void {
    const { url } = storeHandoffPayload(payload);
    window.open(url, "_blank", "noopener,noreferrer");
}

export function navigateWithHandoff(payload: SvgToolHandoffPayloadV1): void {
    const { url } = storeHandoffPayload(payload);
    window.location.assign(url);
}

/**
 * If current URL has ?handoff= and storage matches, removes entry and query.
 * Returns payload only when targetPanel matches `expectedPanel`.
 */
export function tryConsumeHandoffForPanel(
    expectedPanel: PanelType,
): SvgToolHandoffPayloadV1 | null {
    if (typeof window === "undefined") return null;
    const id = readHandoffIdFromUrl();
    if (!id) return null;

    const key = handoffStorageKey(id);
    const raw = localStorage.getItem(key);
    localStorage.removeItem(key);

    if (!raw) {
        stripHandoffQueryFromUrl();
        return null;
    }

    let parsed: unknown;
    try {
        parsed = JSON.parse(raw) as unknown;
    } catch {
        stripHandoffQueryFromUrl();
        return null;
    }

    if (!parsed || typeof parsed !== "object") {
        stripHandoffQueryFromUrl();
        return null;
    }

    const p = parsed as Partial<SvgToolHandoffPayloadV1>;
    if (
        p.v !== 1 ||
        typeof p.targetPanel !== "string" ||
        typeof p.slot !== "string" ||
        typeof p.name !== "string" ||
        typeof p.content !== "string"
    ) {
        stripHandoffQueryFromUrl();
        return null;
    }

    if (p.targetPanel !== expectedPanel) {
        stripHandoffQueryFromUrl();
        return null;
    }

    const slot = p.slot as SvgToolHandoffSlot;
    if (slot !== "path" && slot !== "object" && slot !== "primary") {
        stripHandoffQueryFromUrl();
        return null;
    }

    stripHandoffQueryFromUrl();
    return {
        v: 1,
        targetPanel: p.targetPanel as PanelType,
        slot,
        name: p.name,
        content: p.content,
    };
}
